import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, ButtonGroup, Chip, Field, Icon, IconButton, Input, Modal, Spinner, Textarea, useToast } from "../../components/ui";
import { UserContext } from "../../contexts";
import { usePoll } from "../../hooks/usePoll";
import { ch24ListingId, checkCh24, connectCh24, fetchOutreach, markOutreachSent, queueOutreach, sendCh24Code } from "./dealsData";
import "./ContactSeller.scss";

// Deals → "Contact seller": the first enquiry to a Chrono24 seller goes out from the
// connected Chrono24 account via the sender box, without opening Chrono24. Replies arrive
// by email. Pacing (gap, daily cap) is the API's; one enquiry per listing, ever.

const OutreachContext = createContext(null);

const LOGIN_BUSY = ["pending", "in_progress", "needs_code"];
const errorText = (e, fallback) => e?.response?.data?.message || fallback;

/** "just now" / "5 min ago" / "3 h ago" / "12 Oct". */
const ago = (iso) => {
  if (!iso) return null;
  const min = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min} min ago`;
  if (min < 24 * 60) return `${Math.round(min / 60)} h ago`;
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
};

/**
 * Loads the sender status + every enquiry; re-polls every 10 s while something is moving.
 * `manage` = may connect the sender's Chrono24 login (master admins). A merchant with the
 * "Automated private seller contact" row gets enquiries and the Watchlist only.
 */
export function OutreachProvider({ enabled, manage = true, children }) {
  const [data, setData] = useState(null);
  const [connectOpen, setConnectOpen] = useState(false);
  const [watchlistOpen, setWatchlistOpen] = useState(false);
  const load = useCallback(() => fetchOutreach().then(setData).catch(() => {}), []);
  useEffect(() => { if (enabled) load(); }, [enabled, load]);

  const busy = !!data && (
    data.items.some((i) => i.status === "queued" || i.status === "sending")
    || LOGIN_BUSY.includes(data.sender?.login_state)
  );
  // usePoll (M4.1): same 10 s while healthy, but backs off on errors, stops on
  // 403/404 and pauses in a hidden tab. Rejects (no catch) so the poll sees it.
  const poll = useCallback(() => fetchOutreach().then(setData), []);
  usePoll(poll, { interval: 10000, enabled: !!enabled && busy });

  const value = useMemo(() => ({
    data,
    setData,
    byListing: new Map((data?.items || []).map((i) => [i.ch24_listing_id, i])),
    manage,
    openConnect: manage ? () => setConnectOpen(true) : null,
    openWatchlist: () => setWatchlistOpen(true),
  }), [data, manage]);

  return (
    <OutreachContext.Provider value={value}>
      {children}
      {enabled && manage ? <ConnectModal open={connectOpen} onClose={() => setConnectOpen(false)} /> : null}
      {enabled ? <WatchlistPanel open={watchlistOpen} onClose={() => setWatchlistOpen(false)} /> : null}
    </OutreachContext.Provider>
  );
}

/** Chrono24 listing ids already contacted or queued (a failed attempt does not count). Empty outside a provider. */
// Lives beside OutreachContext, which is private to this file; fast refresh of this file reloads the page instead.
// eslint-disable-next-line react-refresh/only-export-components
export function useContactedListings() {
  const ctx = useContext(OutreachContext);
  return useMemo(() => new Set([...(ctx?.byListing?.entries() || [])].filter(([, i]) => i.status !== "failed").map(([id]) => id)), [ctx]);
}

/** The account's state in one word + a tone, shared by the header pill and both dialogs. */
const accountState = (s) => {
  if (!s) return { tone: "muted", word: "Checking…" };
  if (!s.online) return { tone: "danger", word: "Sender offline" };
  if (s.login_state === "needs_code") return { tone: "warning", word: "Needs code" };
  if (LOGIN_BUSY.includes(s.login_state)) return { tone: "info", word: "Connecting…" };
  if (s.logged_in) return { tone: "success", word: "Connected" };
  return { tone: "warning", word: "Not connected" };
};

/** Header pill: Chrono24 + the account's state; opens the account dialog. */
export function Ch24Status() {
  const ctx = useContext(OutreachContext);
  if (!ctx?.manage) return null;
  const st = accountState(ctx.data?.sender);
  return (
    <button type="button" className={`c24-pill c24-pill--${st.tone}`} onClick={ctx.openConnect} aria-label={`Chrono24 account: ${st.word}`}>
      <span className="c24-pill__dot" aria-hidden="true" />
      <span className="c24-pill__brand">Chrono24</span>
      <span className="c24-pill__state">{st.word}</span>
    </button>
  );
}

const STATUS_ICON = { success: "CheckCircle2", warning: "AlertTriangle", danger: "AlertTriangle", muted: "Lock" };

function ConnectModal({ open, onClose }) {
  const { data, setData } = useContext(OutreachContext);
  const s = data?.sender;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [switching, setSwitching] = useState(false);

  useEffect(() => { if (open && s?.account && !email) setEmail(s.account); }, [open, s?.account]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (!open) { setSwitching(false); setError(""); setPassword(""); } }, [open]);

  const act = async (fn, fallback) => {
    setBusy(true); setError("");
    try { setData(await fn()); return true; } catch (e) { setError(errorText(e, fallback)); return false; } finally { setBusy(false); }
  };
  const connect = async (e) => {
    e.preventDefault();
    await act(() => connectCh24(email.trim(), password), "Could not reach the sender.");
    setPassword(""); setSwitching(false);
  };
  const submitCode = async (e) => {
    e.preventDefault();
    if (await act(() => sendCh24Code(code.trim()), "Could not send the code.")) setCode("");
  };

  const st = accountState(s);
  const needsCode = s?.login_state === "needs_code";
  const working = s?.login_state === "pending" || s?.login_state === "in_progress";
  const showForm = s && !needsCode && !working && (!s.logged_in || switching);
  const sub = !s ? "Loading…"
    : !s.online ? "The sender box is not answering, so nothing can be sent or connected right now."
      : working ? "Logging in to Chrono24. This takes up to a minute."
        : needsCode ? "Chrono24 sent a verification code to the account's email or phone."
          : s.logged_in ? [s.account, s.checked_at ? `checked ${ago(s.checked_at)}` : null].filter(Boolean).join(" · ")
            : "Log in once. The sender keeps the session for every message after.";

  return (
    <Modal open={open} onClose={onClose} title="Chrono24 account" size="sm" className="c24-modal">
      <div className="c24-stack">
        <div className={`c24-status c24-status--${st.tone}`}>
          <span className="c24-status__icon" aria-hidden="true">
            {working ? <Spinner size="sm" label="Connecting" /> : <Icon name={STATUS_ICON[st.tone] || "Lock"} size="md" />}
          </span>
          <span className="c24-status__text">
            <strong>{st.word}</strong>
            <span>{sub}</span>
          </span>
        </div>

        {s?.login_state === "failed" && s.login_error ? <Alert tone="error" title="The last login did not work">{s.login_error}</Alert> : null}
        {error ? <Alert tone="error">{error}</Alert> : null}

        {needsCode ? (
          <form className="c24-stack" onSubmit={submitCode}>
            <Field label="Verification code">
              <Input className="c24-code" value={code} onChange={(e) => setCode(e.target.value)} autoComplete="one-time-code" inputMode="numeric" autoFocus />
            </Field>
            <Button type="submit" fullWidth size="lg" loading={busy} disabled={!code.trim()}>Verify</Button>
          </form>
        ) : null}

        {s?.logged_in && !switching && !working && !needsCode ? (
          <ButtonGroup equal>
            <Button variant="secondary" leadingIcon={<Icon name="RefreshCw" size="sm" />} loading={busy} disabled={!s.online} onClick={() => act(checkCh24, "Could not ask for a check.")}>Check now</Button>
            <Button variant="ghost" onClick={() => setSwitching(true)}>Log in again</Button>
          </ButtonGroup>
        ) : null}

        {showForm ? (
          <form className="c24-stack" onSubmit={connect}>
            <Field label="Email">
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="off" placeholder="you@example.com" />
            </Field>
            <Field label="Password">
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="off" />
            </Field>
            <p className="c24-fine"><Icon name="ShieldCheck" size="sm" /> Used once to log in, then forgotten. Never stored or shown.</p>
            <Button type="submit" fullWidth size="lg" loading={busy} disabled={!email.trim() || !password || !s?.online}>Connect Chrono24</Button>
            {switching ? <Button type="button" variant="ghost" fullWidth onClick={() => setSwitching(false)}>Cancel</Button> : null}
          </form>
        ) : null}

        {s?.probe ? (
          <details className="c24-details">
            <summary>Diagnostics</summary>
            <pre>{JSON.stringify(s.probe, null, 2)}</pre>
          </details>
        ) : null}
      </div>
    </Modal>
  );
}

const STATUS = {
  queued: { tone: "info", label: "Queued", title: "Waiting for its turn to send" },
  sending: { tone: "info", label: "Sending…", title: "Going out now" },
  sent: { tone: "success", label: "Contacted", title: "Open the Chrono24 conversation" },
};

/**
 * The card's contact control for a Chrono24 listing link: a "Contact seller" button,
 * then the enquiry's status (Queued → Contacted, linking to the Chrono24 conversation).
 * Renders nothing outside an OutreachProvider or for a non-Chrono24 link.
 * `summary` = { image, price, meta } shown at the top of the message dialog.
 * `compact` = icon-only controls (Private sellers cards, Alex 2026-10-29: the full-width
 * button took too much room); the words move to the tooltip and the accessible name.
 */
export function ContactSellerAction({ url, label, productId, summary = {}, compact = false }) {
  const ctx = useContext(OutreachContext);
  const [open, setOpen] = useState(false);
  const listingId = ch24ListingId(url);
  if (!ctx || !listingId) return null;
  const item = ctx.byListing.get(listingId);

  if (item && item.status !== "failed") {
    const st = STATUS[item.status] || STATUS.queued;
    const mark = compact
      ? <span className={`c24-state__icon c24-state__icon--${st.tone}`}><Icon name={item.status === "sent" ? "Check" : "Clock"} size="sm" /></span>
      : <Badge tone={st.tone} dot size="sm">{st.label}</Badge>;
    const name = compact ? `${st.label} · ${st.title}` : st.title;
    return item.messenger_url
      ? <a className="c24-state" href={item.messenger_url} target="_blank" rel="noopener noreferrer" title={name} aria-label={compact ? name : undefined}>{mark}</a>
      : <span className="c24-state" title={name} role={compact ? "img" : undefined} aria-label={compact ? name : undefined}>{mark}</span>;
  }
  return (
    <>
      {compact
        ? item
          ? <IconButton size="sm" round label="Retry contacting the seller" title={item.error || "Retry contacting the seller"} onClick={() => setOpen(true)}><Icon name="RefreshCw" size="sm" /></IconButton>
          : <IconButton size="sm" round label="Contact seller" title="Contact seller" className="c24-contact-icon" onClick={() => setOpen(true)}><Icon name="Send" size="sm" /></IconButton>
        : item
          ? <Button size="sm" variant="secondary" leadingIcon={<Icon name="RefreshCw" size="sm" />} title={item.error || undefined} onClick={() => setOpen(true)}>Retry</Button>
          : <Button size="sm" leadingIcon={<Icon name="Send" size="sm" />} onClick={() => setOpen(true)}>Contact seller</Button>}
      {open ? <ContactModal url={url} label={label} productId={productId} summary={summary} failed={item} onClose={() => setOpen(false)} /> : null}
    </>
  );
}

// Three openers to start from; the text stays fully editable.
const PRESETS = [
  { key: "available", label: "Availability", text: (w, n) => `Hello, I'm interested in your ${w}. Is it still available? Could you tell me a little more about its condition and whether it comes with the box and papers? Many thanks${n}` },
  { key: "price", label: "Best price", text: (w, n) => `Hello, I'm interested in your ${w}. Would you consider an offer? Please let me know the best price you could do. Many thanks${n}` },
  { key: "details", label: "Photos & history", text: (w, n) => `Hello, I'm interested in your ${w}. Could you share a few more photos, and tell me about its service history and whether the box and papers are included? Many thanks${n}` },
];

function ContactModal({ url, label, productId, summary, failed, onClose }) {
  const { data, setData, openConnect, manage } = useContext(OutreachContext);
  const { user } = useContext(UserContext) || {};
  const toast = useToast();
  const fill = (p) => p.text(label || "watch", user?.first_name ? `, ${user.first_name}` : "");
  const [message, setMessage] = useState(() => failed?.message || fill(PRESETS[0]));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const s = data?.sender;
  const limits = data?.limits;
  const st = accountState(s);
  const preset = PRESETS.find((p) => fill(p) === message)?.key;

  const send = async () => {
    setBusy(true); setError("");
    try {
      setData(await queueOutreach({
        listing_url: url, message: message.trim(), label, product_id: productId || undefined,
        // Kept with the enquiry, so the Watchlist still shows them after this report moves on.
        image: summary.image || undefined, price: summary.price || undefined, meta: summary.meta || undefined,
      }));
      toast.success("Message queued", { description: "It goes out from Chrono24 within a few minutes." });
      onClose();
    } catch (e) {
      setError(errorText(e, "Could not queue the message."));
      setBusy(false);
    }
  };

  // Replies go to the sender account's Chrono24 inbox. That is staff's own
  // mailbox; a merchant must not be told replies will reach them.
  const replies = manage ? "replies by email" : "replies go to Retail Circle's Chrono24 account";
  const pace = limits
    ? `${limits.sent_today} of ${limits.daily_cap} sent today · ${Math.round(limits.gap_seconds / 60)} min apart · ${replies}`
    : manage ? "Replies arrive by email" : "Replies go to Retail Circle's Chrono24 account";

  return (
    <Modal open onClose={onClose} title={failed ? "Try again" : "Contact seller"} size="md" className="c24-modal"
      footer={(
        <ButtonGroup align="end">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button leadingIcon={<Icon name="Send" size="sm" />} loading={busy} disabled={message.trim().length < 20} onClick={send}>Send message</Button>
        </ButtonGroup>
      )}>
      <div className="c24-stack">
        <div className="c24-watch">
          <span className="c24-watch__img">{summary.image ? <img src={summary.image} alt="" /> : <Icon name="Clock" size="md" />}</span>
          <span className="c24-watch__text">
            <strong>{label || "Chrono24 listing"}</strong>
            {summary.price ? <span className="c24-watch__price">{summary.price}</span> : null}
            {summary.meta ? <span className="c24-watch__meta">{summary.meta}</span> : null}
          </span>
        </div>

        {failed?.error ? <Alert tone="error" title="The last attempt was not sent">{failed.error}</Alert> : null}
        {error ? <Alert tone="error">{error}</Alert> : null}

        <div className={`c24-from c24-from--${st.tone}`}>
          <span className="c24-from__label">From</span>
          <span className="c24-pill__dot" aria-hidden="true" />
          <span className="c24-from__who">{s?.account || (manage ? "Chrono24 account" : "Retail Circle's Chrono24 account")}</span>
          <span className="c24-from__state">{st.word}</span>
          {openConnect && s && !s.logged_in && s.online && !LOGIN_BUSY.includes(s.login_state)
            ? <button type="button" className="c24-link" onClick={() => { onClose(); openConnect(); }}>Connect</button>
            : null}
        </div>
        {s && (!s.online || !s.logged_in) ? (
          <p className="c24-fine">You can send now. It waits in the queue until Chrono24 is {s.online ? "connected" : "back online"}.</p>
        ) : null}

        <div className="c24-presets" role="group" aria-label="Start from">
          {PRESETS.map((p) => (
            <Chip key={p.key} selected={preset === p.key} onClick={() => setMessage(fill(p))}>{p.label}</Chip>
          ))}
        </div>
        <Field label="Message" hint="Chrono24 removes phone numbers, emails and links.">
          <Textarea value={message} onChange={(e) => setMessage(e.target.value)} minRows={5} maxRows={10} maxLength={2000} />
        </Field>
        <p className="c24-fine"><Icon name="Clock" size="sm" /> {pace}</p>
      </div>
    </Modal>
  );
}

// ── Watchlist: every seller ever contacted, whatever day's report they came from ────────────

/** Header button: opens the Watchlist; the count is the sellers contacted so far. */
export function WatchlistButton() {
  const ctx = useContext(OutreachContext);
  if (!ctx) return null;
  const items = ctx.data?.items || [];
  const contacted = items.filter((i) => i.status === "sent").length;
  return (
    <button type="button" className="c24-pill c24-pill--muted c24-wl-btn" onClick={ctx.openWatchlist}
      aria-label={`Watchlist: ${contacted} contacted`}>
      <Icon name="Eye" size="sm" />
      <span className="c24-pill__brand">Watchlist</span>
      {items.length ? <span className="c24-wl-btn__count">{contacted}</span> : null}
    </button>
  );
}

const WL_FILTERS = [
  { key: "all", label: "All", match: () => true },
  { key: "sent", label: "Contacted", match: (i) => i.status === "sent" },
  { key: "queued", label: "Queued", match: (i) => i.status === "queued" || i.status === "sending" },
  { key: "failed", label: "Not sent", match: (i) => i.status === "failed" },
];

const when = (iso) => (iso
  ? new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
  : null);

function WatchlistPanel({ open, onClose }) {
  const { data } = useContext(OutreachContext);
  const [filter, setFilter] = useState("all");
  const items = data?.items || [];
  const shown = items.filter(WL_FILTERS.find((f) => f.key === filter).match);

  return (
    <Modal open={open} onClose={onClose} variant="drawer" size="md" title="Watchlist" className="c24-modal"
      description="Every Chrono24 seller you've contacted from Deals, with the exact message sent.">
      <div className="c24-stack">
        <div className="c24-presets" role="group" aria-label="Show">
          {WL_FILTERS.map((f) => (
            <Chip key={f.key} selected={filter === f.key} onClick={() => setFilter(f.key)} count={items.filter(f.match).length}>{f.label}</Chip>
          ))}
        </div>
        {shown.length === 0 ? (
          <p className="c24-fine">{items.length === 0 ? "No sellers contacted yet. Use Contact seller on a Deals card." : "Nothing here."}</p>
        ) : (
          <ul className="c24-wl" aria-label="Contacted sellers">
            {shown.map((i) => <li key={i.id}><WatchlistItem item={i} /></li>)}
          </ul>
        )}
      </div>
    </Modal>
  );
}

const WL_STATUS = {
  sent: { tone: "success", label: "Contacted" },
  queued: { tone: "info", label: "Queued" },
  sending: { tone: "info", label: "Sending…" },
  failed: { tone: "danger", label: "Not sent" },
};

function WatchlistItem({ item }) {
  const { setData } = useContext(OutreachContext);
  const [marking, setMarking] = useState(false);
  const [link, setLink] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const st = WL_STATUS[item.status] || WL_STATUS.queued;
  const date = item.status === "sent" ? when(item.sent_at) : when(item.created_at);

  const markSent = async () => {
    setBusy(true); setError("");
    try { setData(await markOutreachSent(item.id, link.trim() || undefined)); } catch (e) { setError(errorText(e, "Could not update it.")); setBusy(false); }
  };

  return (
    <article className="c24-wl__item">
      <div className="c24-watch">
        <span className="c24-watch__img">{item.image ? <img src={item.image} alt="" loading="lazy" /> : <Icon name="Clock" size="md" />}</span>
        <span className="c24-watch__text">
          <strong>{item.label || `Chrono24 listing ${item.ch24_listing_id}`}</strong>
          {item.price_label ? <span className="c24-watch__price">{item.price_label}</span> : null}
          {item.meta ? <span className="c24-watch__meta">{item.meta}</span> : null}
        </span>
      </div>
      <div className="c24-wl__status">
        <Badge tone={st.tone} dot size="sm">{st.label}</Badge>
        {date ? <span className="c24-fine">{item.status === "sent" ? "Sent " : "Requested "}{date}</span> : null}
      </div>
      <blockquote className="c24-wl__msg">{item.message}</blockquote>
      {item.status === "failed" && item.error ? <p className="c24-fine c24-wl__err">{item.error}</p> : null}
      <div className="c24-wl__actions">
        {item.listing_url ? (
          <Button size="sm" variant="secondary" href={item.listing_url} target="_blank" rel="noopener noreferrer"
            leadingIcon={<Icon name="ExternalLink" size="sm" />}>Listing</Button>
        ) : null}
        {item.messenger_url ? (
          <Button size="sm" variant="secondary" href={item.messenger_url} target="_blank" rel="noopener noreferrer"
            leadingIcon={<Icon name="MessageCircle" size="sm" />}>Conversation</Button>
        ) : null}
        {item.status === "failed" && !marking ? (
          <Button size="sm" variant="ghost" onClick={() => setMarking(true)} leadingIcon={<Icon name="Check" size="sm" />}>Mark as contacted</Button>
        ) : null}
      </div>
      {marking ? (
        <div className="c24-wl__mark">
          <p className="c24-fine">Only if it reached the seller (you can see it in Chrono24 Messages). Nothing is sent.</p>
          <Field label="Conversation link (optional)">
            <Input value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://www.chrono24.com/user/messenger/…communicationId=…" />
          </Field>
          {error ? <Alert tone="error">{error}</Alert> : null}
          <ButtonGroup align="end">
            <Button size="sm" variant="ghost" onClick={() => { setMarking(false); setError(""); }}>Cancel</Button>
            <Button size="sm" loading={busy} onClick={markSent}>Mark as contacted</Button>
          </ButtonGroup>
        </div>
      ) : null}
    </article>
  );
}
